import { countdownString } from "@faire/deprecated-retailer-visitor-shared/components/CountdownBanner/utils";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useMarketRangeDates } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useMarketRangeDates";
import { setIntervalIfPossible } from "@faire/next-utils/isomorphic/setIntervalIfPossible";
import { differenceInDays } from "date-fns/differenceInDays";
import { History } from "history";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

export interface IProps {
  /** Client should ensure that the given deadline is after the current date. */
  deadline: number | Date;
  DaysBeforeDeadlineView: (props: {
    daysLeft: number;
    timeLeft: string;
    mainEventRangeDate: string;
  }) => React.ReactNode;
  HoursBeforeDeadlineView: (props: { timeLeft: string }) => React.ReactNode;
  LoggedOutView: (props: {
    lastDay: boolean;
    mainEventRangeDate: string;
    history: History;
  }) => React.ReactNode;
  onCountdownChange: () => void;
}

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export const CountdownBannerBase = ({
  deadline,
  DaysBeforeDeadlineView,
  HoursBeforeDeadlineView,
  LoggedOutView,
  onCountdownChange,
}: IProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const deadlineDate = useMemo(() => new Date(deadline), [deadline]);
  const history = useHistory();
  const { latestMarketMainEventRangeDate } = useMarketRangeDates();
  const daysBeforeDeadline = useMemo(
    () => differenceInDays(deadlineDate, currentDate),
    [currentDate, deadlineDate]
  );
  const showHourCountdown = daysBeforeDeadline <= 0;

  useEffect(
    () => onCountdownChange(),
    [currentDate, deadlineDate, onCountdownChange]
  );

  useEffect(() => {
    const interval = setIntervalIfPossible(
      () => setCurrentDate(new Date()),
      1000
    );

    return () => clearInterval(interval);
  }, []);

  if (!isLoggedInRetailer && LoggedOutView) {
    return (
      <LoggedOutView
        lastDay={showHourCountdown}
        mainEventRangeDate={latestMarketMainEventRangeDate}
        history={history}
      />
    );
  }

  if (showHourCountdown) {
    return (
      <HoursBeforeDeadlineView
        timeLeft={countdownString(deadlineDate, currentDate)}
      />
    );
  }

  return (
    <DaysBeforeDeadlineView
      daysLeft={daysBeforeDeadline}
      timeLeft={countdownString(
        deadlineDate,
        new Date(currentDate.getTime() + daysBeforeDeadline * ONE_DAY_IN_MS)
      )}
      mainEventRangeDate={latestMarketMainEventRangeDate}
    />
  );
};
